// @flow

import React, { Component } from "react";
import { connect } from "react-redux";
import {
  SectionComponent,
  TooltipDropdown,
  Row,
  Col,
  GSGrid,
  Loader,
  GSRenderers,
  TabBar,
  Text,
  View,
} from "@components";
import { compose } from "redux";
import { format } from "date-fns";
import actionCreators from "@modules/reporting/actionCreator";
import * as reportingSelectors from "@modules/reporting/selectors";
import { localize } from "redux-i18n";
import type { State } from "@store/reducers";
import type {
  TrendActivity,
  DepartmentOverview,
  ReasonOverview,
} from "@modules/reporting/reducer/dashboard/types";
import type { ValuesItemValue } from "@modules/reporting/reducer/types";
import type { EffectiveParameters } from "@modules/reporting/reducer/focus-lists/department/types";
import { isFirstCareUser, getUserType } from "@modules/account/selectors";
import {
  getIsAbsencesLoading,
  getIsHierarchyBreakdownLoading,
  getHierarchyBreakdown,
} from "@modules/reporting/selectors/dashboard/dashboard-index";
import {
  AbsenceForPrevAndNextTenDays,
  CurrentAbsenceStatusDepartments,
  CurrentOpenAbsencesPerReason,
} from "@modules/reporting/components";
import "./dashboard-absence-now.style.scss";
import type { HierarchyBreakdownItemType } from "@modules/reporting/reducer/dashboard/types.js";

type OwnProps = $Exact<{
  clientId: number,
}>;

type PropsFromState = $Exact<{
  trendActivity: TrendActivity[],
  dontMarkCurrentDay?: boolean,
  departmentOverview: DepartmentOverview[],
  reasonOverview: ReasonOverview[],
  effectiveParameters: ?EffectiveParameters,
  isFirstCareUser: boolean,
  userType: string,
  isAbsencesLoading: boolean,
  isHierarchyBreakdownLoading: boolean,
  hierarchyBreakdown: HierarchyBreakdownItemType[],
}>;

type PropsFromDispatch = $Exact<{
  getAbsenceNow: typeof actionCreators.GetAbsenceNow,
  getHierarchyBreakdown: typeof actionCreators.GetHierarchyBreakdown,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type ComponentState = {
  departmentSectionOpen: boolean,
  trendSectionOpen: boolean,
  reasonSectionOpen: boolean,
};

export class DashboardAbsenceNow extends Component<Props, ComponentState> {
  state = {
    departmentSectionOpen: true,
    trendSectionOpen: true,
    reasonSectionOpen: true,
  };
  componentDidMount() {
    this.props.getHierarchyBreakdown({ clientId: this.props.clientId });
  }

  toggleTrendSection = () => this.setState({ trendSectionOpen: !this.state.trendSectionOpen });

  toggleDepartmentSection = () => {
    this.setState({ departmentSectionOpen: !this.state.departmentSectionOpen });
  };

  toggleReasonSection = () => this.setState({ reasonSectionOpen: !this.state.reasonSectionOpen });

  getChartValue = (dataArray: ValuesItemValue[], key: string): string | number => {
    const findedElement = dataArray.find(el => el.key === key) || { value: "n/a" };
    return findedElement.value;
  };

  get chartDaysData() {
    return this.props.trendActivity.map(el => {
      return [
        format(el.date, "DD/MM"),
        this.getChartValue(el.values, "open_short_term_absences"),
        this.getChartValue(el.values, "open_long_term_absences"),
      ];
    });
  }

  get trendActivityConfig() {
    return {
      leftCols: [
        {
          name: "absenceType",
          title: (
            <div className="title-header d-flex justify-content-center flex-column">
              <div>{this.props.t("DATE")}</div>
            </div>
          ),
        },
      ],
    };
  }

  get trendActivityData(): Array<Object> {
    const lines = [
      { key: "open_short_term_absences", title: this.props.t("SHORT_TERM") },
      { key: "open_long_term_absences", title: this.props.t("LONG_TERM") },
      { key: "total_open_absences", title: this.props.t("TOTAL_ABSENCES_NUMBER") },
      {
        key: "employee_absence_percentage",
        title: this.props.t("STAFF_ABSENT_PERCENTAGE"),
      },
    ];
    const weekdays = {
      absenceType: this.props.t("WEEKDAY"),
      ...this.props.trendActivity.reduce(
        (target, dateStat) =>
          Object.assign(target, {
            [dateStat.date]: format(dateStat.date, "ddd"),
          }),
        {},
      ),
    };

    return [
      ...[weekdays],
      ...lines.map((desc: $Exact<{ key: string, title: string }>) => ({
        absenceType: desc.title,
        ...this.props.trendActivity.reduce(
          (target, dateStat) =>
            Object.assign(target, {
              [dateStat.date]: this.getChartValue(dateStat.values, desc.key),
            }),
          {},
        ),
      })),
    ];
  }

  /*
	============= DEPARTMENT OVERVIEW =============
	*/

  get departmentChartData() {
    return this.props.departmentOverview
      .sort(
        (a: Object, b: Object) =>
          parseInt(this.getChartValue(b.values, "total_open_absences"), 10) -
            parseInt(this.getChartValue(a.values, "total_open_absences"), 10) ||
          a.department.localeCompare(b.department),
      )
      .map(el => [
        el.department,
        this.getChartValue(el.values, "open_short_term_absences"),
        this.getChartValue(el.values, "open_long_term_absences"),
      ])
      .slice(0, 10);
  }

  get departmentOverviewConfig() {
    return;
  }

  get departmentOverviewData() {
    const keys = [
      "head_count",
      "total_open_absences",
      "open_short_term_absences",
      "open_long_term_absences",
      "employees_absent_percentage",
      "staff_available_today",
      "staff_available_tomorrow",
      "outstanding_rtw_interviews",
    ];
    return this.props.departmentOverview
      .map(desc => ({
        departmentName: desc.department,
        ...keys.reduce(
          (acc, curr) =>
            Object.assign(acc, {
              [curr]: this.getChartValue(desc.values, curr),
            }),
          {},
        ),
      }))
      .sort(
        (a: Object, b: Object) =>
          parseInt(b.total_open_absences, 10) - parseInt(a.total_open_absences, 10) ||
          a.departmentName.localeCompare(b.departmentName),
      );
  }

  /*
  ============= REASONS OVERVIEW =============
  */
  get chartReasonData() {
    return this.props.reasonOverview
      .sort((a: Object, b: Object) => {
        return (
          parseInt(this.getChartValue(b.values, "total_open_absences"), 10) -
            parseInt(this.getChartValue(a.values, "total_open_absences"), 10) ||
          b.reason.localeCompare(a.reason)
        );
      })
      .map((el: Object) => {
        return [
          el.reason,
          this.getChartValue(el.values, "open_short_term_absences"),
          this.getChartValue(el.values, "open_long_term_absences"),
        ];
      })
      .slice(0, 10);
  }

  get columns() {
    return [
      {
        name: this.props.t("ABSENCE_REASON"),
        key: "reasonName",
        align: "left",
        frozen: "left",
      },
      {
        name: this.props.t("OPEN_ABSENCES"),
        key: "total_open_absences",
        headerCellClass: "font-weight-bold",
        cellClass: "font-weight-bold p-1px",
        renderCell: ({ row }: { row: Object }) =>
          GSRenderers.barIndicator({
            total: row.total_open_absences || 0,
            max: this.reasonOverviewData.reduce(
              (acc: *, d: Object) => (d.total_open_absences > acc ? d.total_open_absences : acc),
              0,
            ),
          }),
      },
      {
        name: this.props.t("PERCENTAGE_OF_TOTAL"),
        key: "percentage_of_total_absences",
        renderCell: ({ row }: { row: Object }) => `${row.percentage_of_total_absences}%`,
      },
      {
        name: this.props.t("SHORT_TERM"),
        key: "open_short_term_absences",
      },
      {
        name: this.props.t("LONG_TERM"),
        key: "open_long_term_absences",
      },
      {
        name: this.props.t("EST_AVRG_LEN_ABS"),
        key: "estimated_average_length_per_absence",
        renderCell: ({ row }: Object) => {
          return Number.parseFloat(row.estimated_average_length_per_absence).toFixed(2);
        },
      },
      {
        name: this.props.t("EST_TOTAL_DAYS_LOST_FOR_ABS"),
        key: "estimated_total_days_lost",
      },
    ];
  }

  get reasonOverviewData() {
    const keys = [
      "total_open_absences",
      "open_short_term_absences",
      "open_long_term_absences",
      "percentage_of_total_absences",
      "estimated_average_length_per_absence",
      "estimated_total_days_lost",
    ];
    return this.props.reasonOverview
      .map((desc: Object) => ({
        reasonName: desc.reason,
        ...keys.reduce(
          (acc, curr) =>
            Object.assign(acc, {
              [curr]: this.getChartValue(desc.values, curr),
            }),
          {},
        ),
      }))
      .sort(
        (a: Object, b: Object) =>
          parseInt(b.total_open_absences, 10) - parseInt(a.total_open_absences, 10) ||
          b.reasonName.localeCompare(a.reasonName),
      );
  }

  get hasHierarchyBreakdownAccess() {
    return ["FULL_HR_ACCESS", "CLIENT_ADMIN", "ADMIN", "SERVICE_MANAGER"].find(
      element => element === this.props.userType,
    );
  }
  get hierarchyBreakdownRows() {
    let hierarchyBreakdown = [...this.props.hierarchyBreakdown];
    hierarchyBreakdown.forEach((row: Object, idx: number) => {
      let hierarchy = [row.hierarchyArea];

      if (row.parentOuId) {
        const parentRow = hierarchyBreakdown.find(el => el.ouId == row.parentOuId);
        if (parentRow.hierarchy) hierarchy = [...hierarchy, ...parentRow.hierarchy];
        else hierarchy = [...hierarchy, parentRow.hierarchyArea];
      }

      hierarchyBreakdown[idx] = {
        ...row,
        key: row.ouId,
        name: row.hierarchyArea,
        id: row.ouId,
        parentId: row.parentOuId,
        isExpanded: true,
      };
    });
    return hierarchyBreakdown;
  }

  render() {
    return (
      <View padding="1rem 0">
        <TabBar>
          <TabBar.Tab title={this.props.t("DAY_TREND")}>
            <View gap={6} padding="0.5rem 0">
              {this.props.isAbsencesLoading && <Loader inline />}
              <View className="gs-container" flex={1}>
                <AbsenceForPrevAndNextTenDays
                  reportData={{
                    effectiveParameters: this.props.effectiveParameters,
                    chartDaysData: this.chartDaysData,
                  }}
                  title={this.props.t("TREND_ACTIVITY")}
                />
              </View>
              <GSGrid
                className="gs-container"
                rows={this.trendActivityData}
                columns={[
                  {
                    name: this.props.t("DATE"),
                    key: "absenceType",
                    width: 150,
                    align: "left",
                  },
                  ...this.props.trendActivity.map(element => {
                    const isToday = format(new Date(), "DD/MM") == format(element.date, "DD/MM");

                    return {
                      name: format(element.date, "DD/MM"),
                      key: element.date,
                      align: "center",
                      headerCellClass: "p-1px",
                      cellClass: "p-1px",
                      renderHeaderCell: (params: Object) => {
                        return (
                          <div
                            style={{ backgroundColor: isToday && "rgba(237, 118, 48, 0.1)" }}
                            className={`w-100 h-100 d-flex align-items-center justify-content-center`}
                          >
                            {params.column.name}
                          </div>
                        );
                      },
                      renderCell: ({ row }: Object) => {
                        return (
                          <div
                            className="w-100 h-100 d-flex align-items-center justify-content-center"
                            style={{ backgroundColor: isToday && "rgba(237, 118, 48, 0.1)" }}
                          >
                            {row[element.date]}
                          </div>
                        );
                      },
                    };
                  }),
                ]}
              />
            </View>
          </TabBar.Tab>
          <TabBar.Tab title={this.props.t("OPEN_ABSENCES_BY_DEPARTMENT")}>
            <View gap="6" padding="0.5rem 0">
              {this.props.isAbsencesLoading && <Loader inline />}
              <View className="gs-container" flex={1}>
                <CurrentAbsenceStatusDepartments
                  title={this.props.t("OPEN_ABSENCES_BY_DEPARTMENT")}
                  reportData={this.departmentChartData}
                />
              </View>
              {!!this.departmentOverviewData.length && (
                <View className="gs-container">
                  <GSGrid
                    id="department-overview-table"
                    columns={[
                      {
                        name: this.props.t("DEPARTMENT"),
                        key: "departmentName",
                        frozen: "left",
                        align: "left",
                      },
                      {
                        name: this.props.t("HEADCOUNT"),
                        key: "head_count",
                      },
                      {
                        name: this.props.t("OPEN_ABSENCES"),
                        headerCellClass: "font-weight-bold",
                        cellClass: "font-weight-bold p-1px",
                        key: "total_open_absences",
                        renderCell: ({ row }) =>
                          GSRenderers.barIndicator({
                            total: row.total_open_absences || 0,
                            max: this.departmentOverviewData.reduce(
                              (acc: *, d: Object) =>
                                d.total_open_absences > acc ? d.total_open_absences : acc,
                              0,
                            ),
                          }),
                      },
                      {
                        name: this.props.t("SHORT_TERM"),
                        key: "open_short_term_absences",
                      },
                      {
                        name: this.props.t("LONG_TERM"),
                        key: "open_long_term_absences",
                      },
                      {
                        name: this.props.t("EMPL_ABS_PERCENTAGE"),
                        key: "employees_absent_percentage",
                        cellClass: "font-weight-bold p-1px",
                        headerCellClass: "font-weight-bold",
                        renderCell: ({ row }) =>
                          GSRenderers.barIndicator({
                            total: row.employees_absent_percentage || 0,
                            max: this.departmentOverviewData.reduce(
                              (acc: *, d: Object) =>
                                d.employees_absent_percentage > acc
                                  ? d.employees_absent_percentage
                                  : acc,
                              0,
                            ),
                            suffix: "%",
                          }),
                      },
                      {
                        name: this.props.t("PENDING_TASKS"),
                        key: "outstanding_rtw_interviews",
                      },
                    ]}
                    rows={this.departmentOverviewData}
                  />
                </View>
              )}
            </View>
          </TabBar.Tab>
          <TabBar.Tab title={this.props.t("OPEN_ABSENCES_BY_REASON")}>
            <View gap="6" padding="0.5rem 0">
              {this.props.isAbsencesLoading && <Loader inline />}
              <View className="gs-container" flex={1}>
                <CurrentOpenAbsencesPerReason
                  title={this.props.t("OPEN_ABSENCES_BY_ABSENCE_REASON")}
                  reportData={this.chartReasonData}
                />
              </View>
              {this.reasonOverviewData.length > 1 && (
                <View className="gs-container">
                  <GSGrid
                    height={450}
                    columns={this.columns}
                    rows={this.reasonOverviewData}
                    enableVirtualization={false}
                  />
                </View>
              )}
            </View>
          </TabBar.Tab>
          <TabBar.Tab
            title={this.props.t("HIERARCHY_BREAKDOWN")}
            hidden={!this.hasHierarchyBreakdownAccess}
          >
            <View gap="6" padding="0.5rem 0">
              {this.props.isHierarchyBreakdownLoading && <Loader inline />}
              <View className="gs-container">
                <GSGrid
                  columns={[
                    {
                      name: this.props.t("HEADCOUNT"),
                      key: "headcount",
                    },
                    {
                      name: this.props.t("TOTAL_OPEN_ABSENCES"),
                      key: "totalOpenAbsences",
                    },
                    {
                      name: this.props.t("OPEN_ST_ABSENCES"),
                      key: "openStAbsences",
                    },
                    {
                      name: this.props.t("OPEN_LT_ABSENCES"),
                      key: "openLtAbsences",
                    },
                    {
                      name: this.props.t("PERCENTAGE_EMPLOYEE_ABSENT"),
                      key: "employeesAbsentPercent",
                    },
                    {
                      name: this.props.t("STAFF_AVAILABLE_TODAY"),
                      key: "staffAvailableToday",
                    },
                    {
                      name: this.props.t("STAFF_AVAILABLE_TOMORROW"),
                      key: "staffAvailableTomorrow",
                    },
                    {
                      name: this.props.t("OUTSTANDING_RTW_INTERVIEWS"),
                      key: "outstandingRtwInterviews",
                    },
                  ]}
                  rows={this.hierarchyBreakdownRows}
                  groupColumn={{
                    name: this.props.t("HIERARCHY_AREA"),
                    key: "hierarchyArea",
                    width: 300,
                  }}
                />
              </View>
            </View>
          </TabBar.Tab>
        </TabBar>
      </View>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  trendActivity: reportingSelectors.getTrendActivity(state),
  departmentOverview: reportingSelectors.getDepartmentOverview(state),
  reasonOverview: reportingSelectors.getReasonOverview(state),
  effectiveParameters: reportingSelectors.getEffectiveParameters(state),
  isFirstCareUser: isFirstCareUser(state),
  userType: getUserType(state),
  isAbsencesLoading: getIsAbsencesLoading(state),
  isHierarchyBreakdownLoading: getIsHierarchyBreakdownLoading(state),
  hierarchyBreakdown: getHierarchyBreakdown(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getAbsenceNow: actionCreators.GetAbsenceNow,
  getHierarchyBreakdown: actionCreators.GetHierarchyBreakdown,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(DashboardAbsenceNow);
